/**
 * @file path-node-overlay.ts
 * @description Renders the path-node editing UI on a transparent overlay canvas.
 *
 * The overlay sits above drawingCanvas (z-index: 50, pointer-events: none).
 * Mouse events pass through to the main canvas; the coordinator intercepts them
 * via EventBus before they reach the drawing handlers.
 *
 * HTML requirement (added by path-edit-coordinator):
 *   <canvas id="pathNodeOverlay"
 *     style="position:absolute; top:0; left:0; pointer-events:none; z-index:50;">
 *   </canvas>
 */

import { EventBus } from '@hcie/shared';
import type { BezierNode } from '@hcie/shared';

// ─── Visual constants ─────────────────────────────────────────────────────────

const ANCHOR_SIZE    = 8;   // half-side of anchor square, screen px
const HANDLE_RADIUS  = 5;   // handle circle radius, screen px
const PATH_COLOR     = '#4af';
const ANCHOR_FILL    = '#fff';
const ANCHOR_SEL     = '#4af';
const HANDLE_FILL    = '#aff';
const LINE_OPACITY   = 0.5;
const DASH: number[] = [4, 4];

// ─── PathNodeOverlay ─────────────────────────────────────────────────────────

export class PathNodeOverlay {
    private canvas: HTMLCanvasElement | null = null;
    private ctx: CanvasRenderingContext2D | null = null;
    private zoom = 1;
    private _active = false;

    constructor() {
        EventBus.on('PATH_EDIT_ACTIVATED',   () => this.activate());
        EventBus.on('PATH_EDIT_DEACTIVATED', () => this.deactivate());
        EventBus.on('PATH_OVERLAY_UPDATE',   (data: any) => {
            if (this._active) {
                this.render(data.nodes, data.isClosed, data.selectedIndex ?? -1);
            }
        });
        EventBus.on('ZOOM_CHANGED', (zoom: number) => this.setZoom(zoom));
    }

    // ── Lifecycle ─────────────────────────────────────────────────────────────

    public activate(): void {
        this._ensureCanvas();
        if (this.canvas) this.canvas.style.display = 'block';
        this._active = true;
    }

    public deactivate(): void {
        this._active = false;
        if (this.canvas && this.ctx) {
            this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
            this.canvas.style.display = 'none';
        }
    }

    public setZoom(zoom: number): void {
        this.zoom = Math.max(0.01, zoom);
        // Resize the overlay when zoom changes (canvas display dimensions are CSS-transformed)
        this._syncSize();
    }

    // ── Rendering ─────────────────────────────────────────────────────────────

    /**
     * Performs a full re-render of the overlay.
     * @param nodes       Current BezierNode array
     * @param isClosed    Whether the path is closed
     * @param selectedIdx Index of the selected node (-1 = none)
     */
    public render(nodes: BezierNode[], isClosed: boolean, selectedIdx: number): void {
        if (!this.canvas || !this.ctx) return;
        const ctx = this.ctx;
        const z = this.zoom;

        ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
        if (nodes.length === 0) return;

        // ── 1. Dashed path outline ────────────────────────────────────────────
        ctx.save();
        ctx.strokeStyle = PATH_COLOR;
        ctx.lineWidth   = 1;
        ctx.setLineDash(DASH);

        ctx.beginPath();
        ctx.moveTo(nodes[0].x * z, nodes[0].y * z);

        const end = isClosed ? nodes.length : nodes.length - 1;
        for (let i = 0; i < end; i++) {
            const a = nodes[i];
            const b = nodes[(i + 1) % nodes.length];
            const cp1 = a.cp2 ?? a;
            const cp2 = b.cp1 ?? b;
            ctx.bezierCurveTo(
                cp1.x * z, cp1.y * z,
                cp2.x * z, cp2.y * z,
                b.x * z, b.y * z
            );
        }
        if (isClosed) ctx.closePath();
        ctx.stroke();
        ctx.restore();

        // ── 2. Control handle stems & circles ────────────────────────────────
        ctx.save();
        ctx.strokeStyle = PATH_COLOR;
        ctx.fillStyle   = HANDLE_FILL;
        ctx.lineWidth   = 1;
        ctx.globalAlpha = LINE_OPACITY;

        for (let i = 0; i < nodes.length; i++) {
            const n = nodes[i];
            if (n.cp1) {
                ctx.beginPath();
                ctx.moveTo(n.x * z, n.y * z);
                ctx.lineTo(n.cp1.x * z, n.cp1.y * z);
                ctx.stroke();
            }
            if (n.cp2) {
                ctx.beginPath();
                ctx.moveTo(n.x * z, n.y * z);
                ctx.lineTo(n.cp2.x * z, n.cp2.y * z);
                ctx.stroke();
            }
        }

        ctx.globalAlpha = 1.0;
        ctx.setLineDash([]);

        for (let i = 0; i < nodes.length; i++) {
            const n = nodes[i];
            if (n.cp1) {
                ctx.beginPath();
                ctx.arc(n.cp1.x * z, n.cp1.y * z, HANDLE_RADIUS, 0, Math.PI * 2);
                ctx.fill();
                ctx.stroke();
            }
            if (n.cp2) {
                ctx.beginPath();
                ctx.arc(n.cp2.x * z, n.cp2.y * z, HANDLE_RADIUS, 0, Math.PI * 2);
                ctx.fill();
                ctx.stroke();
            }
        }
        ctx.restore();

        // ── 3. Anchor squares ─────────────────────────────────────────────────
        ctx.save();
        ctx.strokeStyle = PATH_COLOR;
        ctx.lineWidth   = 1;
        ctx.setLineDash([]);

        for (let i = 0; i < nodes.length; i++) {
            const n = nodes[i];
            const half = ANCHOR_SIZE / 2;
            ctx.fillStyle = i === selectedIdx ? ANCHOR_SEL : ANCHOR_FILL;
            ctx.beginPath();
            ctx.rect(n.x * z - half, n.y * z - half, ANCHOR_SIZE, ANCHOR_SIZE);
            ctx.fill();
            ctx.stroke();
        }
        ctx.restore();
    }

    // ── Private helpers ───────────────────────────────────────────────────────

    private _ensureCanvas(): void {
        if (this.canvas) return;

        let el = document.getElementById('pathNodeOverlay') as HTMLCanvasElement | null;
        if (!el) {
            el = document.createElement('canvas');
            el.id = 'pathNodeOverlay';
            el.style.cssText = 'position:absolute; top:0; left:0; pointer-events:none; z-index:50; display:none;';
            const wrapper = document.getElementById('canvasWrapper');
            if (wrapper) wrapper.appendChild(el);
        }

        this.canvas = el;
        this.ctx    = el.getContext('2d');
        this._syncSize();
    }

    private _syncSize(): void {
        if (!this.canvas) return;
        const drawingCanvas = document.getElementById('drawingCanvas') as HTMLCanvasElement | null;
        if (drawingCanvas) {
            this.canvas.width  = drawingCanvas.width;
            this.canvas.height = drawingCanvas.height;
        }
    }
}
